import { inject, Injectable } from '@angular/core';
import {
    AG_GRID_VALUE_GETTER_BY_FIELD,
    BadgeRendererComponent,
    BadgeRendererValue,
    BooleanRendererComponent,
    DATE_CELL_COL_DEFS,
    DATE_CELL_VALUE_FORMATTER,
    IconWithTextCellRendererComponent,
    IconWithTextCellRendererParams,
    IdentitiesRendererComponent,
    LifecycleRendererComponent,
    LOCALISED_CURRENCY_VALUE_FORMATTER,
    WdxAgGridCurrencyField,
} from '@wdx/shared/components/ag-grid';
import {
    InvestmentDataColumnConfiguration,
    ViewColumnType,
} from '@wdx/shared/swagger-gen';
import {
    IMeLocale,
    TranslationsService,
    WdxDateFormat,
    WdxDateTimeService,
    WdxNumberService,
} from '@wdx/shared/utils';
import {
    ColDef,
    ICellRendererParams,
    ValueFormatterParams,
    ValueGetterParams,
} from 'ag-grid-community';
import { TRANSLATE_CELL_VALUE } from '@wdx/shared/components/ag-grid';

@Injectable()
export class PortfolioAgGridColumnMapperService {
    private translationsService = inject(TranslationsService);
    private dateTimeService = inject(WdxDateTimeService);
    private localeService = inject(IMeLocale);
    private numberService = inject(WdxNumberService);

    public createColDef(config: InvestmentDataColumnConfiguration): ColDef {
        const field = config.path as string;
        let colDef = {
            headerName: this.translationsService.translateTokenisedString(
                config.name as string,
            ),
            field,
            colId: config.path as string,
            sortable: config.isSortable,
            filter: !!config.isSearchable,
            filterParams: {
                buttons: ['clear', 'apply'],
            },
        } as ColDef;
        switch (config?.fieldType) {
            case ViewColumnType.Answer:
                colDef.valueGetter = (params: ValueGetterParams) =>
                    params.data[field]?.label;

                break;

            case ViewColumnType.Number:
                colDef.cellDataType = 'number';
                break;

            case ViewColumnType.Boolean:
                colDef.cellDataType = 'boolean';
                colDef.cellRendererSelector = (params: ICellRendererParams) => {
                    if (params.node.group) {
                        return;
                    }
                    return {
                        component: BooleanRendererComponent,
                        params: {
                            value: params.data[field],
                        },
                    };
                };
                break;

            case ViewColumnType.Activity:
                colDef = { ...colDef, ...DATE_CELL_COL_DEFS };
                colDef.field = field + '.date';
                colDef.valueFormatter = DATE_CELL_VALUE_FORMATTER(
                    this.dateTimeService,
                    WdxDateFormat.AbsoluteDateTime,
                );
                colDef.filter = config.isSearchable
                    ? 'agDateColumnFilter'
                    : undefined;
                break;

            case ViewColumnType.ClientSummary:
                colDef.minWidth = 85;
                colDef.cellRendererSelector = (
                    params: ICellRendererParams,
                ) => ({
                    component: LifecycleRendererComponent,
                    params: {
                        value: params.data?.clientStatus?.clientLifecycle,
                    },
                });
                break;

            case ViewColumnType.Identities:
                colDef.cellRenderer = IdentitiesRendererComponent;
                break;

            case ViewColumnType.Date:
                colDef.cellDataType = 'dateString';
                colDef.valueFormatter = DATE_CELL_VALUE_FORMATTER(
                    this.dateTimeService,
                    WdxDateFormat.AbsoluteDate,
                );
                colDef.filterParams = {
                    ...colDef.filterParams,
                    filterOptions: ['lessThan', 'greaterThan', 'inRange'],
                };
                break;

            case ViewColumnType.DateTime:
                colDef.cellDataType = 'dateString';
                colDef.valueFormatter = DATE_CELL_VALUE_FORMATTER(
                    this.dateTimeService,
                    WdxDateFormat.AbsoluteDateTime,
                );
                colDef.filterParams = {
                    ...colDef.filterParams,
                    filterOptions: ['lessThan', 'greaterThan', 'inRange'],
                };
                break;

            case ViewColumnType.Text:
                colDef.valueFormatter = TRANSLATE_CELL_VALUE(
                    this.translationsService,
                );
                break;

            case ViewColumnType.Status:
                colDef.cellRendererSelector = (params: ICellRendererParams) => {
                    return {
                        component: BadgeRendererComponent,
                        params: {
                            value: {
                                label: params.data.statusText,
                                mapping: {
                                    completed: 'success',
                                    settled: 'success',
                                    pending: 'warning',
                                    failed: 'danger',
                                },
                            } as BadgeRendererValue,
                        },
                    };
                };
                break;

            case ViewColumnType.Currency:
                colDef.valueGetter = (params: ValueGetterParams) => {
                    const value = params.data[field];

                    if (typeof value === 'number' || !value) {
                        return {
                            value,
                            currency: {
                                currencyCode: params.data['currency']?.isoCode,
                            },
                        };
                    }
                    if (value?.isoCode) {
                        return {
                            value: value?.value,
                            currency: {
                                currencyCode: value?.isoCode,
                            },
                        };
                    }
                    return {
                        value: value?.value,
                        currency: {
                            currencyCode:
                                params.data['currency']?.isoCode ??
                                params.data['currencyIso']?.isoCode,
                        },
                    };
                };
                colDef.filter = config.isSearchable
                    ? 'agNumberColumnFilter'
                    : undefined;
                colDef.filterValueGetter = (params: ValueGetterParams) => {
                    const value = params.data[field];

                    return typeof value === 'number' || !value
                        ? value ?? null
                        : value?.value ?? null;
                };
                colDef.valueFormatter = LOCALISED_CURRENCY_VALUE_FORMATTER(
                    this.numberService,
                    () => this.localeService.getMeLocale(),
                );
                colDef.comparator = (
                    a: WdxAgGridCurrencyField,
                    b: WdxAgGridCurrencyField,
                ) => {
                    const numA = a?.value ?? 0;
                    const numB = b?.value ?? 0;
                    return numA - numB;
                };
                break;

            case ViewColumnType.Duration:
                colDef.valueFormatter = (params: ValueFormatterParams) => {
                    const value = AG_GRID_VALUE_GETTER_BY_FIELD(params);
                    const hoursLabel =
                        this.translationsService.translateTokenisedString(
                            'ENUM_DURATIONTYPE_HOURS',
                        );

                    return typeof value === 'number' && value > 0
                        ? `${(value / 3600).toFixed(2)} ${hoursLabel}`
                        : '';
                };
                break;

            case ViewColumnType.Icon:
                colDef.cellRendererSelector = (
                    params: ICellRendererParams,
                ) => ({
                    component: IconWithTextCellRendererComponent,
                    params: {
                        value: {
                            icon: AG_GRID_VALUE_GETTER_BY_FIELD(params),
                        } as IconWithTextCellRendererParams,
                    },
                });
                break;

            default:
                break;
        }

        return colDef.cellRendererSelector
            ? { ...colDef, field: undefined }
            : colDef;
    }
}
